/**
 * Shared number formatting for customer-facing surfaces (v160).
 *
 * Numbers were formatted ad-hoc across the app — some with `toLocaleString`,
 * many as raw `{n}` interpolation — so large counts rendered without thousands
 * separators (the owner-reported "no commas anywhere" issue). Route ALL
 * customer-facing counts/amounts through these helpers so the formatting is
 * consistent and can't drift per-surface. Guarded by formatNumber.test.ts.
 */

/** Thousands-separated integer for counts: 1234 -> "1,234"; null/NaN -> "0". */
export function formatCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '0'
  return Math.round(n).toLocaleString('en-US')
}

/** Compact for big counts where horizontal space is tight: 12345 -> "12.3K". */
export function formatCompact(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '0'
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n)
}

/** USD money with thousands separators, no cents: 1234567 -> "$1,234,567". */
export function formatUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '$0'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

/**
 * Currency-aware money formatter (v225).
 *
 * The backend captures and stores non-USD competitor prices (EUR/GBP/INR/CAD/…)
 * and ships the ISO currency code in the payload, but several FE surfaces used to
 * hardcode a "$", mislabeling a EUR/GBP/INR list price as US dollars — a
 * data-correctness defect for a product that sells accurate pricing. Route every
 * price display through this so the rendered symbol always matches the captured
 * currency.
 *
 * @param n            The amount. null/NaN renders as an em-dash-free "-".
 * @param currency     ISO 4217 code (e.g. "USD", "EUR", "GBP"). Falls back to USD.
 * @param maxFraction  Fraction-digit ceiling (default 0 — competitor list prices
 *                     are whole-unit; pass 2 for cents).
 */
export function formatCurrency(
  n: number | null | undefined,
  currency: string | null | undefined,
  maxFraction: number = 0
): string {
  if (n == null || !Number.isFinite(n)) return '-'
  const code = (currency || 'USD').toUpperCase()
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: maxFraction,
    }).format(n)
  } catch {
    // Unknown/invalid ISO code: never crash a price display — fall back to the
    // amount with the raw code as a suffix so we still tell the truth about it.
    return `${n.toLocaleString('en-US', { maximumFractionDigits: maxFraction })} ${code}`
  }
}
